import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";

/*
	Container-deposit support. A product is marked in Norce with a boolean
	parametric; the deposit itself is a regular fee product added as a CHILD
	basket line of the product (ParentLineNo), quantity = product quantity ×
	deposit units.
	Norce cascades child deletion when the parent is removed; quantity changes
	are compensated in cartStore. The parametric→fee-product mapping is
	deployment config — when the map is empty the whole feature is off.
*/

export interface DepositConfig {
	/** Norce parametric Id → deposit fee product PartNo. */
	parametricPartNoMap: Record<string, string>;
	/** Norce parametric Id holding the deposit units per product (24 for a 24-pack). 0 = not used. */
	unitsParametricId: number;
}

export interface DepositMeta {
	depositPartNo: string;
	perUnit: number;
}

interface IdValueLike {
	Id?: number;
	Value?: string | null;
}

/**
 * Anything we can read parametrics from: a full Norce Product / variant
 * (`Parametrics`/`VariantParametrics`) or a listing item
 * (`ParametricTextField` + `ParametricValueSeed`).
 */
export interface DepositParametricSource {
	Parametrics?: IdValueLike[] | null;
	VariantParametrics?: IdValueLike[] | null;
	ParametricTextField?: IdValueLike[] | null;
	ParametricValueSeed?: string | null;
}

export const isDepositEnabled = (
	config: Partial<DepositConfig> | undefined | null
): config is DepositConfig =>
	Object.keys(config?.parametricPartNoMap ?? {}).length > 0;

export const isDepositItem = (
	item: Partial<BasketItem> | undefined,
	config: Partial<DepositConfig> | undefined | null
) => {
	if (!item?.PartNo || !isDepositEnabled(config)) return false;
	return Object.values(config.parametricPartNoMap).includes(item.PartNo);
};

const TRUTHY_PARAMETRIC_VALUES = ["true", "1", "yes", "ja"];

const isTruthyParametricValue = (value: string | null | undefined) =>
	TRUTHY_PARAMETRIC_VALUES.includes(
		String(value ?? "")
			.trim()
			.toLowerCase()
	);

const readParametricValue = (
	source: DepositParametricSource,
	parametricId: number
): string | undefined => {
	const lists = [
		source.Parametrics,
		source.VariantParametrics,
		source.ParametricTextField
	];
	for (const list of lists) {
		const match = list?.find((p) => p?.Id === parametricId);
		if (match?.Value != null) return match.Value;
	}
	// Listing items may carry value-type parametrics as an "id:value" seed.
	const seedPair = source.ParametricValueSeed?.split(",")
		.map((pair) => pair.trim())
		.find((pair) => pair.startsWith(`${parametricId}:`));
	return seedPair ? seedPair.slice(seedPair.indexOf(":") + 1) : undefined;
};

/**
 * Resolve a product's deposit meta from the first source that carries a
 * truthy deposit parametric (pass variant first, then parent). Returns null
 * when no deposit parametric is set.
 */
export const getDepositMeta = (
	sources: (DepositParametricSource | undefined | null)[],
	config: DepositConfig
): DepositMeta | null => {
	for (const source of sources) {
		if (!source) continue;
		for (const [parametricId, depositPartNo] of Object.entries(
			config.parametricPartNoMap
		)) {
			const flagValue = readParametricValue(source, Number(parametricId));
			if (!isTruthyParametricValue(flagValue)) continue;

			const antalValue = config.unitsParametricId
				? readParametricValue(source, config.unitsParametricId)
				: undefined;
			const antal = Number.parseInt(String(antalValue ?? ""), 10);
			// Missing, unparsable or non-positive units fall back to 1: a
			// product marked with a deposit parametric must never charge
			// zero deposit.
			return {
				depositPartNo,
				perUnit: Number.isFinite(antal) && antal > 0 ? antal : 1
			};
		}
	}
	return null;
};

/** Total deposit amount (inc VAT) of the deposit fee lines in a basket. */
export const getDepositTotalIncVat = (
	items: BasketItem[] | undefined,
	config: Partial<DepositConfig> | undefined | null
): number =>
	(items ?? [])
		.filter((item) => isDepositItem(item, config))
		.reduce(
			(sum, item) =>
				sum + (item.PriceDisplayIncVat ?? 0) * (item.Quantity ?? 0),
			0
		);
